import {useState, useEffect, useMemo} from 'react'
import {appendTrackingParamsToUrl, getTrackingParamsFromQuery} from 'notability-services-common-universal'

/**
 * Returns the current URL's tracking params as a Record.
 * Only includes params from the TRACKING_LINK_PARAMS whitelist, sanitized.
 * Uses useEffect to read from window.location after hydration, avoiding SSR/hydration mismatch.
 */
export function useCurrentTrackingParams(): Record<string, string> {
  const [trackingParams, setTrackingParams] = useState<Record<string, string>>({})

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(window.location.search))
    setTrackingParams(getTrackingParamsFromQuery(params))
  }, [])

  return trackingParams
}

/**
 * Appends the current URL's tracking params to a given base URL.
 * Use this when constructing links that navigate to another service
 * (e.g., marketing → login) to preserve UTM params across the hop.
 */
export function useUrlWithTrackingParams(baseUrl: string): string {
  const trackingParams = useCurrentTrackingParams()
  return useMemo(() => appendTrackingParamsToUrl(baseUrl, trackingParams), [baseUrl, trackingParams])
}
